import { gql } from '@bariendo/lib/graphql';

/**
 * Which axis the global patient search resolved the typed input to.
 *
 * The three are mutually exclusive: the input is classified once and a single query is issued.
 * An OR across all three was rejected because `email co` on a short fragment matches a large
 * fraction of the patient book and crowds the name match out of a ten-row dropdown.
 */
export enum PatientSearchBranch {
  NAME = 'name',
  PHONE = 'phone',
  EMAIL = 'email',
}

export interface PatientSearchQuery {
  branch: PatientSearchBranch;
  /** The normalized term that was searched, for highlighting the matched value on the row. */
  term: string;
  query: string;
  variables: Record<string, string>;
}

/** Below this many digits the input is likelier a name fragment than a phone number. */
const MIN_PHONE_DIGITS = 4;

/** `email co` on one or two characters matches most of the patient book. */
const MIN_EMAIL_LENGTH = 3;

const PATIENT_FIELDS = gql`
  resourceType
  id
  name {
    given
    family
  }
  birthDate
  telecom {
    system
    value
  }
  extension {
    url
    valueString
    # PAYMENT_STATUS_EXTENSION's status, at and payor, for the Connect popover (BAR-3084).
    extension {
      url
      valueCode
      valueString
      valueDateTime
    }
  }
  meta {
    tag {
      system
      code
    }
  }
`;

/**
 * The name branch stays on the plain `name` search param rather than `_filter … sw`, so the
 * behaviour staff already rely on is preserved exactly.
 */
const NAME_QUERY = gql`
  query SearchPatientsByName($name: String) {
    PatientList(name: $name, _count: 10) {
      ${PATIENT_FIELDS}
    }
  }
`;

/**
 * `phone` and `email` are exact token matches with no normalization — a bare ten-digit query
 * never matches an E.164-stored value — so the contains operator in `_filter` is the only
 * option. GraphQL does not expose the `:contains` modifier as an argument.
 */
const FILTER_QUERY = gql`
  query SearchPatientsByFilter($filter: String) {
    PatientList(_filter: $filter, _count: 10) {
      ${PATIENT_FIELDS}
    }
  }
`;

/**
 * `_filter` is a string grammar, not a parameterized query: an unbalanced quote is a 400 from
 * the server rather than an empty result. Both characters that can break out of the quoted
 * literal are removed.
 */
function sanitize(input: string): string {
  return input.replace(/["\\]/g, '').trim();
}

/**
 * Stored values are a mix of E.164 and bare ten digits, so the query has to be the digit run
 * common to both: a leading country code would match the former and miss the latter.
 */
function toPhoneDigits(input: string): string {
  const digits = input.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

export function buildPatientSearchQuery(input: string): PatientSearchQuery | undefined {
  const sanitized = sanitize(input);
  if (!sanitized) return undefined;

  if (sanitized.includes('@')) {
    if (sanitized.length < MIN_EMAIL_LENGTH) return undefined;
    return {
      branch: PatientSearchBranch.EMAIL,
      term: sanitized,
      query: FILTER_QUERY,
      variables: { filter: `email co "${sanitized}"` },
    };
  }

  const digits = toPhoneDigits(sanitized);
  if (digits.length >= MIN_PHONE_DIGITS) {
    return {
      branch: PatientSearchBranch.PHONE,
      term: digits,
      query: FILTER_QUERY,
      variables: { filter: `phone co "${digits}"` },
    };
  }

  return {
    branch: PatientSearchBranch.NAME,
    term: sanitized,
    query: NAME_QUERY,
    variables: { name: sanitized },
  };
}
